import { useEffect, useState } from 'react'
import { Share2 } from 'lucide-react'
import { Sheet } from './UI'
import { useAuth } from '../hooks/useAuth'
import { useEpisodeRatings } from '../hooks/useEpisodeRatings'
import { myReactionsForShow } from '../lib/episodeReaction'
import { seasonStats, wrapLine, wrapHeadline } from '../lib/seasonWrap'
import { tmdbGet, posterUrl } from '../lib/tmdb'
import { logEvent } from '../lib/analytics'
import { useShareCard } from '../hooks/useShareCard'

// ── Season wrap — your season, told back to you ──────────────────────────────
// Ratings and moods had no destination between a mark and the Final Cut at series end
// (2026-10-24 read: 136 ratings, 105 moods, nothing shown back). Finish a season and,
// after the "Season N, done." clapboard drops, this sheet opens once: every episode as
// a bar of your reels with the mood you picked beneath it, the standout, one honest
// line. It is also a door on the show page ("Your S2 ✦") for any season you've
// finished. Your own data only; nothing here is the room's.
const SEEN = (show, season) => `nextep_season_wrap_${show}_${season}`

export function SeasonWrapSheet({ target, onClose }) {
  const { user } = useAuth()
  const { ratings } = useEpisodeRatings(target?.showId, user?.id)
  const [episodes, setEpisodes] = useState(null)
  const [moods, setMoods] = useState({})
  const [shareLabel, shareCard, sharing] = useShareCard()
  useEffect(() => {
    let alive = true
    if (!target?.showId) return
    tmdbGet(`/tv/${target.showId}/season/${target.season}`).then(d => { if (alive) setEpisodes(d?.episodes || []) }).catch(() => { if (alive) setEpisodes([]) })
    if (user?.id) myReactionsForShow(user.id, target.showId).then(m => { if (alive) setMoods(m) })
    logEvent('season_wrap', { show: target.showId, season: target.season, src: target.src || 'auto' })
    return () => { alive = false }
  }, [target?.showId, target?.season, user?.id])   // eslint-disable-line react-hooks/exhaustive-deps
  if (!target) return null
  const st = seasonStats({ episodes: episodes || [], ratings, moods, season: target.season })
  const line = wrapLine(st)
  const share = async () => {
    logEvent('season_wrap_share', { show: target.showId, season: target.season })
    await shareCard({ show: { id: target.showId, name: target.showName, poster_path: target.poster }, eyebrow: `Season ${target.season} · wrapped`, headline: target.showName, line: line || `Season ${target.season}` })
  }
  const max = 5
  return (
    <Sheet open onClose={onClose} label={`Your season ${target.season} of ${target.showName}`} from="bottom" panelStyle={{ padding: '10px 20px calc(30px + env(safe-area-inset-bottom))', background: 'var(--bg-raised)' }}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', marginTop: 8 }}>
        {target.poster && <img src={posterUrl(target.poster, 'w154')} alt="" style={{ width: 56, height: 84, borderRadius: 10, objectFit: 'cover', flexShrink: 0, boxShadow: '0 10px 24px rgba(0,0,0,0.45)' }} />}
        <div style={{ minWidth: 0 }}>
          <div className="gm-eyebrow" style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--amber)' }}>Season {target.season} · wrapped</div>
          <div style={{ fontFamily: 'var(--font-serif)', fontSize: 26, color: 'var(--text-strong)', lineHeight: 1.1, marginTop: 4, letterSpacing: '-0.01em' }}>{wrapHeadline(st)}</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 5 }}>{target.showName}{line ? ` · ${line}` : ''}</div>
        </div>
      </div>

      {/* The strip: one bar per episode, your reels as height, your mood beneath. */}
      <div style={{ marginTop: 18, padding: '14px 12px 10px', borderRadius: 14, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        {episodes == null ? <div style={{ height: 96 }} /> : (
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 110, overflowX: 'auto', scrollbarWidth: 'none' }}>
            {st.bars.map((b, i) => {
              const h = b.rating != null ? Math.max(10, Math.round((b.rating / max) * 70)) : 6
              const top = st.standout && st.standout.ep === b.ep && st.rated > 1
              return (
                <div key={b.ep} className="nx-wrap-bar" style={{ '--d': `${i * 45}ms`, flex: '1 0 22px', minWidth: 22, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 4, height: '100%' }} title={`E${b.ep} ${b.name}${b.rating != null ? ` · ${b.rating} reels` : ''}`}>
                  <div style={{ width: '100%', height: h, borderRadius: 5, background: b.rating != null ? (top ? 'var(--amber)' : 'color-mix(in srgb, var(--amber) 55%, transparent)') : 'rgba(var(--ink-rgb),0.12)', boxShadow: top ? '0 0 12px rgba(var(--amber-rgb),0.55)' : 'none' }} />
                  <div style={{ height: 16, fontSize: 13, lineHeight: 1 }}>{b.mood || ''}</div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-dim)' }}>{b.ep}</div>
                </div>
              )
            })}
          </div>
        )}
        {st.standout && st.rated > 1 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-muted)' }}><span style={{ color: 'var(--amber)', fontWeight: 700 }}>Standout</span> · E{st.standout.ep} “{st.standout.name}” · {st.standout.rating} reels</div>
        )}
        {episodes && st.rated === 0 && !st.topMood && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: 'var(--text-dim)' }}>Rate episodes as you go and this fills in. Tap a watched ring on the show page to rate it late.</div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button type="button" onClick={share} disabled={sharing} aria-busy={sharing || undefined} className="ui-hit" style={{ flex: 1, height: 44, borderRadius: 'var(--radius-pill)', border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 600, cursor: sharing ? 'default' : 'pointer', opacity: sharing ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}><Share2 size={15} /> {shareLabel}</button>
        <button type="button" onClick={onClose} className="ui-hit" style={{ flex: 1, height: 44, borderRadius: 'var(--radius-pill)', border: 'none', background: 'var(--amber)', color: 'var(--amber-ink)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>On to season {target.season + 1}</button>
      </div>
      <style>{`@keyframes nxWrapBar { from { opacity: 0; transform: translateY(10px) scaleY(.6) } to { opacity: 1; transform: none } } .nx-wrap-bar { animation: nxWrapBar .5s var(--ease-out) both; animation-delay: var(--d, 0ms); transform-origin: bottom } @media (prefers-reduced-motion: reduce) { .nx-wrap-bar { animation: none } }`}</style>
    </Sheet>
  )
}

// Opens the sheet: once, after the season clapboard drops (nextep:wrap type=season →
// nextep:wrap-done), and on demand from the show page (nextep:open-season-wrap).
export function SeasonWrapHost() {
  const { user } = useAuth()
  const [target, setTarget] = useState(null)
  useEffect(() => {
    if (!user?.id) return
    let pending = null
    const onWrap = (e) => { const d = e.detail || {}; if (d.type === 'season' && d.showId && d.season) pending = { showId: d.showId, season: d.season, showName: d.showName, poster: d.poster, src: 'auto' } }
    const onDone = () => {
      const p = pending; pending = null
      if (!p) return
      try { if (localStorage.getItem(SEEN(p.showId, p.season))) return; localStorage.setItem(SEEN(p.showId, p.season), '1') } catch {}
      setTimeout(() => { if (!window.__nextepModalHold) setTarget(p) }, 700)
    }
    const onOpen = (e) => { const d = e.detail || {}; if (d.showId && d.season) setTarget({ ...d, src: d.src || 'manual' }) }
    window.addEventListener('nextep:wrap', onWrap)
    window.addEventListener('nextep:wrap-done', onDone)
    window.addEventListener('nextep:open-season-wrap', onOpen)
    return () => { window.removeEventListener('nextep:wrap', onWrap); window.removeEventListener('nextep:wrap-done', onDone); window.removeEventListener('nextep:open-season-wrap', onOpen) }
  }, [user?.id])
  if (!target) return null
  return <SeasonWrapSheet target={target} onClose={() => setTarget(null)} />
}
